import { Box, Paper, Typography, Divider, Tabs, Tab, IconButton, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useState, useMemo, useCallback } from 'react';
import { useBandStore } from '../../stores/bandStore';
import BandList from '../BandList/BandList';
import EnhancedQueue from '../Player/EnhancedQueue';
import FavoritesList from '../Favorites/FavoritesList';

const DesktopLayout = () => {
  const [middleTab, setMiddleTab] = useState(0);

  // Optimized: only re-compute when these specific values change
  const bands = useBandStore((state) => state.bands);
  const selectedBandsSet = useBandStore((state) => state.selectedBands);
  const selectedBandYears = useBandStore((state) => state.selectedBandYears);
  const selectedBandMonthYears = useBandStore((state) => state.selectedBandMonthYears);
  const toggleBandSelection = useBandStore((state) => state.toggleBandSelection);

  const selectedBands = useMemo(() => {
    return bands.filter(band => selectedBandsSet.has(`${band.collection}|${band.source}`));
  }, [bands, selectedBandsSet]);

  const handleRemoveBand = useCallback((band: typeof bands[0]) => {
    toggleBandSelection(band);
  }, [toggleBandSelection]);

  const isBandCustomized = useCallback((band: typeof bands[0]) => {
    const years = selectedBandYears.get(band.bandName);
    const monthYears = selectedBandMonthYears.get(band.bandName);
    const hasYears = years && years.size > 0;
    const hasMonthYears = monthYears && monthYears.size > 0;
    return !!(hasYears || hasMonthYears);
  }, [selectedBandYears, selectedBandMonthYears]);

  const getCustomizationSummary = useCallback((band: typeof bands[0]) => {
    const years = selectedBandYears.get(band.bandName);
    const monthYears = selectedBandMonthYears.get(band.bandName);
    const parts: string[] = [];
    
    if (years && years.size > 0) {
      parts.push(`${years.size} year${years.size > 1 ? 's' : ''}`);
    }
    if (monthYears && monthYears.size > 0) {
      parts.push(`${monthYears.size} month`);
    }
    
    return parts.join(', ');
  }, [selectedBandYears, selectedBandMonthYears]);

  const renderSelectedBands = () => {
    return (
      <Box
        sx={{
          height: '100%',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1, p: 2 }}>
          {selectedBands.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No bands selected
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Click bands on the left to add them to your shuffle
              </Typography>
            </Box>
          ) : (
            <Box>
              {selectedBands.map((band) => (
                <Paper
                  key={`${band.collection}|${band.source}|${band.bandIdx}`}
                  elevation={1}
                  sx={{
                    mb: 1.5,
                    p: 2,
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                    transition: 'all 0.2s ease-in-out',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                    '&:hover': {
                      bgcolor: 'rgba(66, 165, 245, 0.08)',
                    },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
                      {band.bandName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                      {band.numShows} shows • {band.numSongs} songs
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {band.source === 'great78' && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'warning.main', 
                            fontWeight: 500,
                          }}
                        >
                          Great 78 Collection
                        </Typography>
                      )}
                      {isBandCustomized(band) && (
                        <Chip
                          label={`Customized: ${getCustomizationSummary(band)}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            '& .MuiChip-label': {
                              px: 0.75,
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveBand(band)}
                    sx={{
                      color: 'text.secondary',
                      opacity: 0.6,
                      p: 0.5,
                      '&:hover': {
                        color: 'error.main',
                        opacity: 1,
                        bgcolor: 'rgba(239, 83, 80, 0.08)',
                      },
                    }}
                    title="Remove from selection"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Paper>
              ))}
            </Box>
          )}
        </Box>

        <Divider sx={{ mx: 2 }} />

        <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Tip:</strong> Click the three dots next to any band to customize which years or months to include.
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Box sx={{ flex: 1, p: 2, overflow: 'hidden' }}>
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: '380px 340px 1fr', 
          gap: 2,
          height: '100%',
        }}>
          {/* Band List Column */}
          <Paper
            elevation={1}
            sx={{
              height: '100%',
              overflow: 'auto',
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <BandList />
          </Paper>

          {/* Selected Bands / Favorites Column */}
          <Paper 
            elevation={1}
            sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column', 
              overflow: 'hidden',
              borderRadius: 2,
            }}
          >
            <Tabs
              value={middleTab}
              onChange={(_, v) => setMiddleTab(v)}
              variant="fullWidth"
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider',
                minHeight: 48,
                bgcolor: 'background.paper',
              }}
            >
              <Tab 
                label={`Selected (${selectedBands.length})`} 
                sx={{ minHeight: 48, textTransform: 'none' }} 
              />
              <Tab 
                label="Favorites" 
                sx={{ minHeight: 48, textTransform: 'none' }} 
              />
            </Tabs>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {middleTab === 0 ? renderSelectedBands() : <FavoritesList />}
            </Box>
          </Paper>

          {/* Queue Column */}
          <Box sx={{ height: '100%', overflow: 'hidden' }}>
            <EnhancedQueue />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DesktopLayout;
